import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { v2 } from '@deque/billing-service-client';
import { ColumnHeader, Notice } from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';

import {
  type SupportedIntegrationProductSlugs,
  ProductSlugs
} from '../../../../common/constants';
import {
  getIntegrationProductInfoFromSlug,
  type IntegrationProductInfo
} from '../../../../common/utils/get-integration-product-name-from-slug';
import ScrimmedLoader from '../../../../common/components/ScrimmedLoader';
import {
  type IntegrationConnection,
  getIntegrationConnections
} from '../../../../common/utils/integrations-client/jira/connections';
import styles from './IntegrationConfigurations.css';
import { useAuthContext } from '../../../../common/contexts/auth';
import ContentToast from '../../../../common/components/ContentToast';
import { type ErrorOrNull } from '../../../../../typings/utils';
import type { HttpError } from '../../../../common/api-client';
import JiraParagonIntegrationConfigurations from './jira-paragon/JiraParagonIntegrationConfigurations';
import JiraOnPremIntegrationConfigurations from './jira-on-prem/JiraOnPremIntegrationConfigurations';
import PageTitle from '../../../../common/components/PageTitle';
import JiraDirectIntegrationConfigurations from './jira-direct/JiraDirectIntegrationConfigurations';
import CustomIntegrationConfigurations from './custom/CustomIntegrationConfigurations';

export type ProductSlugWithConnections = {
  productSlug: ProductSlugs;
  connections: IntegrationConnection[];
  integrationProductInfo: IntegrationProductInfo;
};

interface IntegrationConfigurationsProps {
  enabledIntegrationProductSlugs: SupportedIntegrationProductSlugs[];
  activeEnterprise: v2.Enterprise;
  isEnterpriseAdmin: boolean;
}

const splitConnectionsByProductSlug = (
  enabledIntegrationProductSlugs: SupportedIntegrationProductSlugs[],
  connections: IntegrationConnection[] | null
): ProductSlugWithConnections[] => {
  const formattedConnections: ProductSlugWithConnections[] = [];

  if (connections !== null) {
    // Organize connections by slug
    const connectionsOrganizedByProductSlug = new Map<
      ProductSlugs,
      IntegrationConnection[]
    >();
    for (const connection of connections) {
      const connectionsList = connectionsOrganizedByProductSlug.get(
        connection.product_slug as unknown as ProductSlugs
      );
      if (connectionsList) {
        connectionsList.push(connection);
      } else {
        connectionsOrganizedByProductSlug.set(
          connection.product_slug as unknown as ProductSlugs,
          [connection]
        );
      }
    }

    // Reformat organized connections in a way that will be easier to render
    for (const enabledIntegrationProductSlug of enabledIntegrationProductSlugs) {
      const productSlug =
        enabledIntegrationProductSlug as unknown as ProductSlugs;
      const connectionsList =
        connectionsOrganizedByProductSlug.get(productSlug);

      formattedConnections.push({
        productSlug,
        connections: connectionsList || [],
        integrationProductInfo: getIntegrationProductInfoFromSlug(productSlug)
      });
    }
  }

  return formattedConnections;
};

function IntegrationConfigurations({
  enabledIntegrationProductSlugs,
  activeEnterprise,
  isEnterpriseAdmin
}: IntegrationConfigurationsProps) {
  const { t } = useTranslation();

  // Get info required for making API calls
  const { user: authUser } = useAuthContext();

  // Get all connections for this enterprise,
  // keep it null until it's loaded, so that transformation function can skip executing when it's null
  const [connections, setConnections] = useState<
    IntegrationConnection[] | null
  >(null);
  const [connectionsError, setConnectionsError] = useState<ErrorOrNull>(null);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionsReloadCounter, setConnectionsReloadCounter] = useState(0);

  useEffect(() => {
    async function fetchConnections() {
      if (activeEnterprise && authUser) {
        try {
          setConnectionsLoading(true);
          const connectionsList = await getIntegrationConnections({
            enterpriseId: activeEnterprise.id,
            token: authUser.token
          });
          setConnections(connectionsList);
        } catch (error) {
          const errorMessage =
            (error as HttpError).status === 402
              ? t('The integration subscription needs to be assigned.')
              : t('Failed to load integration information.');

          setConnectionsError(new Error(errorMessage));
        } finally {
          setConnectionsLoading(false);
        }
      }
    }

    fetchConnections();
  }, [activeEnterprise, authUser, connectionsReloadCounter]);

  // A trick to force reload in useEffect above
  const reloadConnections = useCallback(() => {
    setConnectionsReloadCounter(value => value + 1);
  }, [setConnectionsReloadCounter]);

  // Organize connections by product slug
  const connectionsByProductSlug = useMemo<ProductSlugWithConnections[]>(() => {
    return splitConnectionsByProductSlug(
      enabledIntegrationProductSlugs,
      connections
    );
  }, [connections]);

  // Make sure that we loaded required data. User must always be loaded by now
  if (connectionsLoading) {
    return <ScrimmedLoader label={t('Loading connections...')} />;
  }

  // Handle errors, inlcuding an unexpcted case of activeEnterprise or authUser being null by now
  if (connectionsError || !activeEnterprise || !authUser) {
    return (
      <ContentToast show={true} type="caution">
        {connectionsError?.message ||
          t('Failed to load integration information.')}
      </ContentToast>
    );
  }

  return (
    <>
      <PageTitle title={t('Configuration - Integrations')} />
      <ColumnHeader>
        <h2>{t('Integrations')}</h2>
      </ColumnHeader>

      {!isEnterpriseAdmin && (
        <div className={styles.banner}>
          <Notice
            type="info"
            title={t(
              'Integration connection settings are managed by your admin.'
            )}
          />
        </div>
      )}

      <div>
        {connectionsByProductSlug.map(connectionByProductSlug => (
          <Fragment key={connectionByProductSlug.productSlug}>
            {connectionByProductSlug.productSlug ===
              ProductSlugs.jiraIntegration && (
              <JiraParagonIntegrationConfigurations
                connectionByProductSlug={connectionByProductSlug}
                activeEnterprise={activeEnterprise}
                isEnterpriseAdmin={isEnterpriseAdmin}
                authUser={authUser}
                reloadConnections={reloadConnections}
              />
            )}

            {connectionByProductSlug.productSlug ===
              ProductSlugs.jiraDirectCloudIntegration && (
              <JiraDirectIntegrationConfigurations
                connectionByProductSlug={connectionByProductSlug}
                activeEnterprise={activeEnterprise}
                isEnterpriseAdmin={isEnterpriseAdmin}
                authUser={authUser}
                reloadConnections={reloadConnections}
              />
            )}

            {connectionByProductSlug.productSlug ===
              ProductSlugs.jiraOnPremIntegration && (
              <JiraOnPremIntegrationConfigurations
                connectionByProductSlug={connectionByProductSlug}
                activeEnterprise={activeEnterprise}
                isEnterpriseAdmin={isEnterpriseAdmin}
                authUser={authUser}
                reloadConnections={reloadConnections}
              />
            )}
            {connectionByProductSlug.productSlug ===
              ProductSlugs.customIntegration && (
              <CustomIntegrationConfigurations
                connectionByProductSlug={connectionByProductSlug}
                activeEnterprise={activeEnterprise}
                isEnterpriseAdmin={isEnterpriseAdmin}
                authUser={authUser}
                reloadConnections={reloadConnections}
              />
            )}
          </Fragment>
        ))}
      </div>
    </>
  );
}

export default IntegrationConfigurations;
